<script setup>
import { Link } from '@inertiajs/vue3'
import ClickToActionSection from '@/components/ClickToActionSection.vue'
import { WrenchScrewdriverIcon } from '@heroicons/vue/24/solid'
import { index as contactIndex } from '@/actions/App/Http/Controllers/ContactController'
import { index as projectIndex } from '@/actions/App/Http/Controllers/ProjectController'
import ProjectCard from '@/pages/projects/partials/ProjectCard.vue'
import ServiceCard from '@/pages/services/Partials/ServiceCard.vue'

defineProps({
    projects: {
        type: Array,
        required: true,
    },

    serviceName: {
        type: String,
        required: true,
    },
})

const seo = {
    title: 'Plynoinstalace',
    description:
        'Bezpečné plynoinstalace - instalace plynových rozvodů, montáž kotlů, revize a servis plynových zařízení. Certifikovaní odborníci s osvědčením pro práci s plynem.',
}

const services = [
    {
        icon: WrenchScrewdriverIcon,
        name: 'Instalace plynových rozvodů',
        description: 'Bezpečná realizace plynových rozvodů pro domácnosti i komerční objekty.',
        features: [
            'Kompletní instalace plynových rozvodů',
            'Dodržení bezpečnostních norem a předpisů',
            'Odborné provedení a kontrola systému',
            'Použití kvalitních a certifikovaných materiálů',
        ],
    },
    {
        icon: WrenchScrewdriverIcon,
        name: 'Připojení plynových spotřebičů',
        description: 'Bezpečné napojení všech běžných plynových zařízení.',
        features: [
            'Připojení kotlů, sporáků, trub, ohřívačů',
            'Kontrola těsnosti spojů',
            'Ověření správné funkce zařízení',
            'Kontrola bezpečnostních prvků',
            'Odborné a bezpečné provedení',
        ],
    },
    {
        icon: WrenchScrewdriverIcon,
        name: 'Opravy a havarijní zásahy',
        description: 'Rychlé a bezpečné řešení závad na plynových instalacích.',
        features: [
            'Opravy plynových rozvodů a zařízení',
            'Řešení úniků plynu',
            'Okamžitý zásah při haváriích',
            'Zajištění bezpečnosti objektu',
            'Minimalizace rizik a škod',
        ],
    },
]

const benefits = [
    { value: '24h', label: 'Havarijní služba' },
    { value: '30+', label: 'Let zkušeností' },
    { value: '100%', label: 'Záruka na práci' },
]
</script>

<template>
    <Head :title="seo.title">
        <meta
            name="description"
            :content="seo.description"
        />
    </Head>

    <section>
        <!-- Hero Section -->
        <div class="relative isolate overflow-hidden bg-gray-900">
            <img
                src="/images/kalianko-team.png"
                alt="Plynoinstalace - profesionální plynoinstalační služby"
                class="absolute inset-0 -z-10 size-full object-cover opacity-20"
            />
            <div class="absolute inset-0 -z-10 bg-linear-to-b from-gray-900/50 via-gray-900/80 to-gray-900" />

            <div class="mx-auto max-w-4xl px-6 py-32 text-center sm:py-40 lg:px-8 lg:py-56">
                <h1 class="text-4xl font-bold tracking-tight text-white sm:text-6xl">Plynoinstalace</h1>

                <p class="mx-auto mt-6 max-w-2xl text-lg/8 text-gray-300">
                    Bezpečné a certifikované plynoinstalační služby. Od instalace rozvodů a připojení spotřebičů po
                    revize a havarijní opravy. Vaše bezpečnost je naší prioritou.
                </p>

                <div class="mt-10 flex flex-col items-center justify-center gap-2 lg:flex-row">
                    <Link
                        :href="contactIndex().url + '#demand-form'"
                        class="primary-link inline-flex w-full items-center justify-center rounded-full text-lg lg:w-fit"
                    >
                        Nezávazná poptávka
                    </Link>
                    <Link
                        :href="projectIndex().url + '?service=' + serviceName"
                        class="inline-flex w-full items-center justify-center rounded-full border border-white/30 bg-white/10 px-6 py-3 text-lg font-semibold text-white backdrop-blur-lg transition hover:bg-white/20 sm:w-auto"
                    >
                        Naše realizace
                    </Link>
                </div>

                <!-- Quick benefits -->
                <div class="mt-16 grid grid-cols-3 gap-4 border-t border-white/10 pt-10">
                    <div
                        v-for="benefit in benefits"
                        :key="benefit.label"
                        class="text-center"
                    >
                        <div class="text-brand-primary text-3xl font-bold">{{ benefit.value }}</div>
                        <div class="mt-1 text-sm text-gray-400">{{ benefit.label }}</div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Features Section -->
        <div class="section-width space-y-20 px-2 py-24 sm:py-32 lg:px-0">
            <div class="mx-auto max-w-2xl text-center">
                <p class="text-brand-primary text-sm font-semibold tracking-wider uppercase">Naše služby</p>
                <h2 class="heading-2 mt-2">Co pro vás můžeme udělat</h2>
                <p class="paragraph mt-4">
                    Nabízíme kompletní vodoinstalační služby od prvotního návrhu až po finální realizaci a následný
                    servis. Ke každé zakázce přistupujeme individuálně, s důrazem na kvalitu provedení, funkčnost,
                    dlouhou životnost a spolehlivý provoz. Pomůžeme vám navrhnout optimální řešení přesně podle vašich
                    potřeb a zajistíme jeho bezproblémovou realizaci.
                </p>
            </div>

            <div class="mt-16 grid grid-cols-1 gap-8 lg:grid-cols-3">
                <ServiceCard
                    v-for="service in services"
                    :key="service.name"
                    :service="service"
                />
            </div>

            <ClickToActionSection />

            <div
                v-if="projects.length > 0"
                class="space-y-5"
            >
                <div class="mx-auto max-w-2xl lg:mx-0">
                    <h2 class="heading-2">Projekt z plynoinstalace</h2>
                    <p class="paragraph mt-4">
                        Podívejte se na některé z našich nedávných realizací v oblasti plynoinstalace. Každý projekt je
                        pro nás výzvou k předvedení našich schopností a odbornosti.
                    </p>
                </div>
                <div class="grid auto-rows-fr grid-cols-1 gap-8 lg:grid-cols-3">
                    <div
                        v-for="project in projects"
                        :key="project.id"
                        class="group"
                    >
                        <ProjectCard :project="project" />
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>
